/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useState } from "react";
import { MessageSquareText, Check, X, HelpCircle, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";

export const CONSENT_SOURCES = [
  { v: "verbal", zh: "咨询口头同意", en: "Verbal (consultation)" },
  { v: "written", zh: "书面同意", en: "Written" },
  { v: "contract_sign", zh: "签约页勾选", en: "Contract-sign checkbox" },
  { v: "other", zh: "其他", en: "Other" },
];
export const EXTRA_SOURCES = [
  { v: "sub_onboard", zh: "分包自助页勾选", en: "Vendor onboarding" },
  { v: "careers_form", zh: "招聘申请勾选", en: "Careers form" },
  { v: "sms_stop", zh: "客户回复 STOP", en: "Customer texted STOP" },
  { v: "sms_start", zh: "客户回复 START", en: "Customer texted START" },
];
export const VENDOR_SOURCES = CONSENT_SOURCES.filter((s) => s.v !== "contract_sign");
const STATUS_UI = {
  agreed: { zh: "已同意接收短信", en: "SMS consent: yes", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Check },
  declined: { zh: "拒绝接收短信", en: "SMS consent: declined", cls: "bg-red-50 text-red-700 border-red-200", Icon: X },
  unknown: { zh: "短信同意未确认", en: "SMS consent: unknown", cls: "bg-slate-50 text-slate-500 border-slate-200", Icon: HelpCircle },
};
export const consentStatus = (e) => e?.smsConsentStatus || (e?.smsConsent === true ? "agreed" : e?.smsConsent === false ? "declined" : "unknown");
export const consentSourceLabel = (v, zh) => { const s = [...CONSENT_SOURCES, ...EXTRA_SOURCES].find((x) => x.v === v); return s ? (zh ? s.zh : s.en) : v || ""; };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "2-digit", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export function SmsConsentBadge({ lead, zh, testid = "crm-sms-consent-badge" }) {
  const st = consentStatus(lead);
  const u = STATUS_UI[st];
  return <span data-testid={testid} data-status={st} className={`inline-flex items-center gap-1 text-[11px] border rounded-full px-2 py-0.5 ${u.cls}`}><MessageSquareText className="h-3 w-3" />{zh ? u.zh : u.en}</span>;
}

/* 记录客户 / 分包商是否同意接收短信（只记录 + 徽章，不拦截发送；Twilio 自动处理 STOP）。entity: lead 或 sub company；endpoint 默认线索接口 */
export function SmsConsentPanel({ lead, entity, zh, api, canEdit, onSaved, endpoint, sources, compact, testid = "crm-sms-consent" }) {
  const T = (z, e) => (zh ? z : e);
  const ent = entity || lead;
  const url = endpoint || `/crm/leads/${ent?.id}/sms-consent`;
  const SRC = sources || CONSENT_SOURCES;
  const st = consentStatus(ent);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ status: st === "unknown" ? "agreed" : st, source: SRC.some((x) => x.v === ent?.smsConsentSource) ? ent.smsConsentSource : SRC[0].v, note: ent?.smsConsentNote || "" });
  const [busy, setBusy] = useState(false);
  const save = async (override) => {
    setBusy(true);
    try {
      const { data } = await api.patch(url, override || f);
      onSaved?.({ ...ent, ...data });
      toast.success(T("已记录短信同意状态", "SMS consent recorded"));
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <div className={`${compact ? "mt-1.5 pt-1.5" : "mt-3 pt-3"} border-t border-slate-100 flex flex-wrap items-center gap-2 text-xs`} data-testid={`${testid}-panel`}>
      <SmsConsentBadge lead={ent} zh={zh} testid={`${testid}-badge`} />
      {(ent?.unsubscribed || ent?.emailUnsub) && <span data-testid={`${testid}-email-unsub`} className="inline-flex items-center gap-1 text-[11px] border rounded-full px-2 py-0.5 bg-orange-50 text-orange-700 border-orange-200" title={ent.emailUnsubAt || ""}>{T("邮件已退订", "Email unsubscribed")}</span>}
      {st !== "unknown" && <span className="text-slate-400" data-testid={`${testid}-meta`}>{consentSourceLabel(ent.smsConsentSource, zh)}{ent.smsConsentAt ? ` · ${fmt(ent.smsConsentAt)}` : ""}{ent.smsConsentByName ? ` · ${ent.smsConsentByName}` : ""}{ent.smsConsentNote ? ` · ${ent.smsConsentNote}` : ""}</span>}
      {canEdit && !open && <button type="button" data-testid={`${testid}-edit`} onClick={() => setOpen(true)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-indigo-600 hover:underline">{st === "unknown" ? T("记录同意", "Record consent") : T("修改", "Edit")}</button>}
      {canEdit && open && (
        <div className="flex flex-wrap items-center gap-1.5 w-full sm:w-auto" data-testid={`${testid}-form`}>
          <select data-testid={`${testid}-status`} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="h-7 rounded border border-slate-200 bg-white px-1.5">
            <option value="agreed">{T("同意接收", "Agreed")}</option><option value="declined">{T("拒绝接收", "Declined")}</option>
          </select>
          <select data-testid={`${testid}-source`} value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} className="h-7 rounded border border-slate-200 bg-white px-1.5">
            {SRC.map((s) => <option key={s.v} value={s.v}>{zh ? s.zh : s.en}</option>)}
          </select>
          <input data-testid={`${testid}-note`} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder={T("备注（如：6/12 电话中口头同意）", "Note")} className="h-7 rounded border border-slate-200 px-2 w-48" />
          <Button size="sm" data-testid={`${testid}-save`} disabled={busy} onClick={() => save()} className="h-7 bg-indigo-600 hover:bg-indigo-700 text-white">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : T("保存", "Save")}</Button>
          {st !== "unknown" && <button type="button" data-testid={`${testid}-clear`} disabled={busy} onClick={() => save({ status: "unknown" })} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-red-600">{T("清除记录", "Clear")}</button>}
          <button type="button" data-testid={`${testid}-cancel`} onClick={() => setOpen(false)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-slate-700">{T("取消", "Cancel")}</button>
        </div>
      )}
      {!compact && <span className="text-[10px] text-slate-400 ml-auto hidden sm:inline">{T("仅记录，不拦截发送；客户回复 STOP 由 Twilio 自动屏蔽并即时同步到这里", "Record only; STOP replies are blocked by Twilio and synced here")}</span>}
    </div>
  );
}
